import React, { FC, useMemo } from 'react';
import { formatDateTime } from 'src/utils/date';

import { generateColumn } from 'src/common/components/table/LegacyTable';
import {
  TableSortableColumnHeader,
  TableSortableColumnHeaderOrderType,
  TableSortableColumnHeaderSortType,
} from 'src/common/components/table/sortable-column-header';
import { getNumOfNodes } from 'src/utils/journey';
import { DashboardHomeOrganizationTemplateActions } from './actions';
import { DataTable } from 'src/common/components/table/data-table';
import { formatNumber } from 'src/utils/text';

export type DashboardHomeOrganizationTemplatesListHeaderTypes =
  | 'name'
  | 'steps'
  | 'creator'
  | 'created_at'
  | 'updated_at'
  | 'id';

export type DashboardHomeOrganizationTemplatesListHeaderSortType =
  TableSortableColumnHeaderSortType<DashboardHomeOrganizationTemplatesListHeaderTypes>;

interface DashboardLibraryOrganizationTemplatesListProps {
  templates: any[];
  sort_by?: string;
  sort_order?: string;
  onTableHeaderClick: (stack: DashboardHomeOrganizationTemplatesListHeaderSortType) => void;
  onDelete: (template: any) => void;
}

export const DashboardLibraryOrganizationTemplatesList: FC<DashboardLibraryOrganizationTemplatesListProps> = ({
  templates,
  sort_by,
  sort_order,
  onTableHeaderClick,
  onDelete,
}) => {
  const formatDate = (datetime: any) => (datetime ? formatDateTime(datetime) : '-');

  const columns = useMemo(
    () => [
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'id'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='id'
            onClick={onTableHeaderClick}
            title='ID'
          />
        ),
        accessor: 'Template ID',
        width: 100,
        Cell: ({ index }: { index: number }) => <span>{templates[index].id}</span>,
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'name'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='name'
            onClick={onTableHeaderClick}
            title='Name'
          />
        ),
        accessor: 'name',
        width: 300,
        Cell: ({ index }: { index: number }) => <span>{templates[index].name}</span>,
      }),
      generateColumn({
        Header: 'Steps',
        accessor: 'steps',
        width: 61,
        Cell: ({ index }: { index: number }) => formatNumber(getNumOfNodes(templates[index].journey)),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'creator'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='creator'
            onClick={onTableHeaderClick}
            title='Creator'
          />
        ),
        accessor: 'creator',
        width: 252,
        Cell: ({ index }: { index: number }) => (
          <span className='text-bedrock-p text-bedrock-black'>{templates[index].creator?.name}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'created_at'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='created_at'
            onClick={onTableHeaderClick}
            title='Created'
          />
        ),
        accessor: 'created_at',
        width: 71,
        Cell: ({ index }: { index: number }) => (
          <span className='text-bedrock-p text-bedrock-black'>{formatDate(templates[index].created_at)}</span>
        ),
      }),
      generateColumn({
        Header: (
          <TableSortableColumnHeader
            isActive={sort_by === 'updated_at'}
            activeOrderType={sort_order as TableSortableColumnHeaderOrderType}
            accessor='updated_at'
            onClick={onTableHeaderClick}
            title='Updated'
          />
        ),
        accessor: 'updated_at',
        width: 71,
        Cell: ({ index }: { index: number }) => (
          <span className='text-bedrock-p text-bedrock-black'>{formatDate(templates[index].updated_at)}</span>
        ),
      }),
      generateColumn({
        Header: '',
        accessor: 'actions',
        width: '100%',
        Cell: ({ index }: { index: number }) => (
          <div className='text-bedrock-p text-bedrock-black mr-2'>
            <div className='flex grow-0 justify-end'>
              <DashboardHomeOrganizationTemplateActions
                template={templates[index]}
                onDelete={() => onDelete(templates[index])}
              />
            </div>
          </div>
        ),
      }),
    ],
    [sort_by, sort_order, templates, onTableHeaderClick]
  );

  return (
    <DataTable
      tableClassName='w-full'
      columns={columns}
      data={templates}
      columnClasses={['', '', '', 'break-words', '', '', '']}
      onRowClick={(row) => {
        console.log('clicked on row', row);
      }}
    />
  );
};